// ============================================
// P0-③ 场景树（2026-10-15）：树数据模型 + 子系统注册表（纯函数，可单测）
// 分组：相机 / 灯光 / 模型 / 天空·环境（子系统按激活状态出现）
// 见 docs/…/3D编辑器升级-P0详细落地架构与P1P2接口契约-20261015.md §2.1
// ============================================

export type SceneNodeKind = 'object' | 'subsystem' | 'category';
export type SceneObjectType = 'model' | 'light' | 'camera' | 'group' | 'measure' | 'annotation' | 'chart3d';

export interface SceneTreeNode {
  /** 稳定 id：'model:<id>' | 'light:<id>' | 'cam:path:<id>' | 'cam:main' | 'cam:birdseye'
   *           'sub:sky' | 'sub:fog' | … | 'cat:models' 等 */
  id: string;
  kind: SceneNodeKind;
  label: string;
  /** antd 图标 key（由视图层映射） */
  icon?: string;
  children?: SceneTreeNode[];
  /** 分类徽标数量 */
  count?: number;
  /** 眼睛（object：model.visible / light.enabled） */
  visible?: boolean;
  /** 子系统激活（enabled） */
  active?: boolean;
  selectable?: boolean;
  /** 摘要（子系统） */
  badges?: string[];
  /** 子系统点击跳转的 activePanel key */
  panel?: string;
  /** 可拖拽重排（模型节点） */
  draggable?: boolean;
  /** 可删除（危险项二次确认） */
  removable?: boolean;
  objectType?: SceneObjectType;
  objectId?: string;
}

export interface SceneTreeModel {
  id: string;
  name?: string;
  visible?: boolean;
  format?: string;
}
export interface SceneTreeLight {
  id: string;
  name?: string;
  type: string;
  enabled?: boolean;
}
export interface SceneTreePathCamera {
  id: string;
  name?: string;
}
export interface SceneTreeGroup {
  id: string;
  name: string;
  /** 组内模型 id（来自 layerStore 图层组） */
  modelIds: string[];
}

export interface SceneTreeInput {
  models: SceneTreeModel[];
  lights: SceneTreeLight[];
  pathCameras: SceneTreePathCamera[];
  /** 编组（图层组，type='group'） */
  groups?: SceneTreeGroup[];
  /** 2026-10-17（标绘层 S2）：标绘对象（子系统摘要） */
  annotations?: { id: string; name?: string; kind?: string }[];
  /** 2026-10-17（体积元素 S1）：体积元素（子系统摘要） */
  volumes?: { id: string; name?: string }[];
  /** P0：场景内多图表对象 */
  charts3d?: { id: string; name?: string; visible?: boolean }[];
  /** 测量项（保留原「场景」树中的测量节点，避免功能回退） */
  measurements?: { id: string; label: string }[];
  browseRouteLength: number;
  displayRegion: { x1: number; z1: number; x2: number; z2: number } | null;
  effects: {
    sky?: { enabled?: boolean };
    weather?: { enabled?: boolean; type?: string };
    dayNight?: { enabled?: boolean; timeOfDay?: number };
  };
  environment: {
    fogEnabled?: boolean;
    /** 2026-10-16（P1-2）：雾参数（nested；旧扁平字段由 loadEnvironment 迁移） */
    fog?: { density?: number };
  };
  /** 鸟瞰相机（P0-④；未启用/未接线时不显示节点） */
  birdsEye?: { enabled?: boolean };
  /** 主相机 key（P0-④；默认 editor） */
  mainCamera?: string;
}

/** 子系统注册表（P0 7 项；P1/P2 新子系统在此登记即可出现在树上） */
export interface SubsystemDescriptor {
  key: string;
  label: string;
  icon: string;
  panel: string;
  isActive: (input: SceneTreeInput) => boolean;
  summary: (input: SceneTreeInput) => string[];
}

export const SUBSYSTEM_REGISTRY: SubsystemDescriptor[] = [
  {
    key: 'sky',
    label: '天空',
    icon: 'sky',
    panel: 'environment',
    isActive: (i) => i.effects.sky?.enabled === true,
    summary: () => ['动态天空'],
  },
  {
    key: 'fog',
    label: '雾',
    icon: 'fog',
    panel: 'environment',
    isActive: (i) => i.environment.fogEnabled === true,
    summary: (i) => [`浓度 ${Number(i.environment.fog?.density ?? 0).toFixed(3)}`],
  },
  {
    key: 'weather',
    label: '天气',
    icon: 'weather',
    panel: 'effects',
    isActive: (i) => i.effects.weather?.enabled === true,
    summary: (i) => [i.effects.weather?.type || '无'],
  },
  {
    key: 'roam',
    label: '漫游',
    icon: 'roam',
    panel: 'camera',
    isActive: (i) => i.pathCameras.length > 0,
    summary: (i) => [`${i.pathCameras.length} 相机 / ${Math.max(0, i.pathCameras.length - 1)} 段`],
  },
  {
    key: 'fpsRoute',
    label: '自动浏览',
    icon: 'route',
    panel: 'camera',
    isActive: (i) => i.browseRouteLength > 0,
    summary: (i) => [`${i.browseRouteLength} 个浏览点`],
  },
  {
    key: 'displayRegion',
    label: '展示区域',
    icon: 'region',
    panel: 'scene',
    isActive: (i) => i.displayRegion != null,
    summary: (i) => {
      const r = i.displayRegion;
      if (!r) return [];
      return [`${Math.abs(r.x2 - r.x1).toFixed(0)} × ${Math.abs(r.z2 - r.z1).toFixed(0)} m`];
    },
  },
  {
    key: 'annotations',
    label: '标绘',
    icon: 'annotation',
    panel: 'environment',
    isActive: (i) => (i.annotations?.length ?? 0) > 0,
    summary: (i) => [`${i.annotations?.length ?? 0} 个标绘`],
  },
  {
    key: 'volumes',
    label: '体积',
    icon: 'volume',
    panel: 'environment',
    isActive: (i) => (i.volumes?.length ?? 0) > 0,
    summary: (i) => [`${i.volumes?.length ?? 0} 个体积`],
  },
  {
    key: 'dayNight',
    label: '昼夜',
    icon: 'dayNight',
    panel: 'effects',
    isActive: (i) => i.effects.dayNight?.enabled === true,
    summary: (i) => [`时间 ${(Number(i.effects.dayNight?.timeOfDay ?? 0)).toFixed(1)}`],
  },
];

export const SUBSYSTEM_KEYS = SUBSYSTEM_REGISTRY.map((s) => s.key);

/** 构建场景树（分类 → 对象/子系统）。空分类隐藏（相机恒有主相机）。 */
export function buildSceneTree(input: SceneTreeInput): SceneTreeNode[] {
  const groups = input.groups ?? [];
  const groupedModelIds = new Set(groups.flatMap((g) => g.modelIds));
  const modelNode = (m: SceneTreeModel): SceneTreeNode => ({
    id: `model:${m.id}`,
    kind: 'object',
    objectType: 'model',
    objectId: m.id,
    label: m.name || '未命名模型',
    icon: 'model',
    visible: m.visible !== false,
    selectable: true,
    draggable: true,
    removable: true,
  });

  // 模型分类：编组节点（含子模型）+ 未编组模型
  const modelChildren: SceneTreeNode[] = [];
  groups.forEach((g) => {
    const kids = input.models.filter((m) => g.modelIds.includes(m.id));
    if (!kids.length) return;
    modelChildren.push({
      id: `group:${g.id}`,
      kind: 'object',
      objectType: 'group',
      objectId: g.id,
      label: g.name,
      icon: 'group',
      count: kids.length,
      selectable: true,
      removable: true,
      children: kids.map(modelNode),
    });
  });
  input.models.filter((m) => !groupedModelIds.has(m.id)).forEach((m) => modelChildren.push(modelNode(m)));

  const cameraChildren: SceneTreeNode[] = [
    {
      id: 'cam:main',
      kind: 'object',
      objectType: 'camera',
      objectId: 'main',
      label: input.mainCamera && input.mainCamera !== 'editor' ? `主相机（${input.mainCamera}）` : '主相机',
      icon: 'camera',
      selectable: true,
    },
  ];
  if (input.birdsEye?.enabled) {
    cameraChildren.push({
      id: 'cam:birdseye',
      kind: 'object',
      objectType: 'camera',
      objectId: 'birdseye',
      label: '鸟瞰相机',
      icon: 'camera',
      selectable: true,
    });
  }
  input.pathCameras.forEach((c, i) => {
    cameraChildren.push({
      id: `cam:path:${c.id}`,
      kind: 'object',
      objectType: 'camera',
      objectId: c.id,
      label: c.name || `漫游相机 ${i + 1}`,
      icon: 'camera',
      selectable: true,
      removable: true,
    });
  });

  const lightChildren: SceneTreeNode[] = input.lights.map((l) => ({
    id: `light:${l.id}`,
    kind: 'object',
    objectType: 'light',
    objectId: l.id,
    label: l.name || l.type,
    icon: 'light',
    visible: l.enabled !== false,
    selectable: true,
    removable: true,
  }));

  const subsystemChildren: SceneTreeNode[] = SUBSYSTEM_REGISTRY
    .filter((s) => s.isActive(input))
    .map((s) => ({
      id: `sub:${s.key}`,
      kind: 'subsystem',
      label: s.label,
      icon: s.icon,
      active: true,
      badges: s.summary(input),
      panel: s.panel,
      selectable: true,
      removable: true,
    }));

  const roots: SceneTreeNode[] = [];
  roots.push({
    id: 'cat:camera',
    kind: 'category',
    label: '相机',
    icon: 'camera',
    count: cameraChildren.length,
    children: cameraChildren,
  });  if (lightChildren.length) {
    roots.push({ id: 'cat:lights', kind: 'category', label: '灯光', icon: 'light', count: lightChildren.length, children: lightChildren });
  }
  if (modelChildren.length) {
    roots.push({ id: 'cat:models', kind: 'category', label: '模型', icon: 'model', count: input.models.length, children: modelChildren });
  }
  // 2026-10-17（标绘层 S2）：标绘分类
  const annotations = input.annotations ?? [];
  if (annotations.length) {
    roots.push({
      id: 'cat:annotations',
      kind: 'category',
      label: '标绘',
      icon: 'annotation',
      count: annotations.length,
      children: annotations.map((m) => ({
        id: `anno:${m.id}`,
        kind: 'object',
        objectType: 'annotation' as const,
        objectId: m.id,
        label: m.name || '标绘',
        icon: 'annotation',
        visible: true,
        selectable: true,
        removable: true,
      })),
    });
  }
  const charts3d = input.charts3d ?? [];
  if (charts3d.length) {
    roots.push({
      id: 'cat:charts3d',
      kind: 'category',
      label: '3D 图表',
      icon: 'chart3d',
      count: charts3d.length,
      children: charts3d.map((chart) => ({
        id: `chart3d:${chart.id}`,
        kind: 'object',
        objectType: 'chart3d' as const,
        objectId: chart.id,
        label: chart.name || '3D 图表',
        icon: 'chart3d',
        visible: chart.visible !== false,
        selectable: true,
        removable: true,
      })),
    });
  }
  const measurements = input.measurements ?? [];
  if (measurements.length) {
    roots.push({
      id: 'cat:measurements',
      kind: 'category',
      label: '测量',
      icon: 'measure',
      count: measurements.length,
      children: measurements.map((m) => ({
        id: `measure:${m.id}`,
        kind: 'object',
        objectType: 'measure' as const,
        objectId: m.id,
        label: m.label,
        icon: 'measure',
        selectable: true,
      })),
    });
  }
  if (subsystemChildren.length) {
    roots.push({ id: 'cat:subsystems', kind: 'category', label: '天空·环境', icon: 'subsystem', count: subsystemChildren.length, children: subsystemChildren });
  }
  // 2026-10-15：根「场景」节点（点击 → 场景属性面板 SceneSettingsPanel，P0-② 入口①）
  return [{
    id: 'scene:root',
    kind: 'category',
    label: '场景',
    icon: 'scene',
    count: flattenCount(roots),
    children: roots,
  }];
}

/** 统计对象节点数（根节点徽标） */
function flattenCount(nodes: SceneTreeNode[]): number {
  let n = 0;
  const walk = (list: SceneTreeNode[]) => list.forEach((x) => {
    if (x.kind === 'object') n += 1;
    if (x.children) walk(x.children);
  });
  walk(nodes);
  return n;
}

/** 扁平化（供搜索/查找） */
export function flattenTree(nodes: SceneTreeNode[]): SceneTreeNode[] {
  const out: SceneTreeNode[] = [];
  const walk = (list: SceneTreeNode[]) => list.forEach((n) => { out.push(n); if (n.children) walk(n.children); });
  walk(nodes);
  return out;
}

/** 按 id 查找节点 */
export function findNode(nodes: SceneTreeNode[], id: string): SceneTreeNode | null {
  return flattenTree(nodes).find((n) => n.id === id) ?? null;
}

/** 分类下的模型节点 id 顺序（拖拽重排用） */
export function modelOrderInTree(nodes: SceneTreeNode[]): string[] {
  const models = findNode(nodes, 'cat:models');
  if (!models?.children) return [];
  const out: string[] = [];
  const walk = (list: SceneTreeNode[]) => list.forEach((n) => {
    if (n.objectType === 'model' && n.objectId) out.push(n.objectId);
    if (n.children) walk(n.children);
  });
  walk(models.children);
  return out;
}
